import { Anchor, Card, Group, rem, Stack, Text, Box } from '@mantine/core';
import type { ComponentProps } from 'react';
import { formatCurrency, utc } from '@vrplatform/utils';
import { LedgerStatementRevenueSummary } from './LedgerStatementRevenueSummary';
import { StatementInfo } from './StatementInfo';
import type { OwnerStatement } from '../../statement/_types';
import { usePayoutDetailDrawer } from '../../../../drawers';

type Props = {
  info: ComponentProps<typeof StatementInfo>;
  summary: ComponentProps<typeof LedgerStatementRevenueSummary>;
  payouts?: OwnerStatement['payouts'];
  currency?: OwnerStatement['currency'];
};

export const LedgerStatementHeader = ({
  info,
  summary,
  payouts = [],
  currency,
}: Props) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing.xl,
        paddingTop: theme.spacing.lg,
        marginBottom: theme.spacing.xl,
        '& > *': {
          width: '100%',
          flexGrow: 1,
        },
      })}
    >
      <StatementInfo {...info} />
      <Box
        sx={(theme) => ({
          flex: 0.7,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <LedgerStatementRevenueSummary {...summary} />
        {payouts.length > 0 && info.type === 'propertyManager' && (
          <StatementPayoutSummary payouts={payouts} currency={currency} />
        )}
      </Box>
    </Box>
  );
};

const StatementPayoutSummary = ({
  payouts,
  currency,
}: {
  payouts: OwnerStatement['payouts'];
  currency?: OwnerStatement['currency'];
}) => {
  const { open: openPayoutDetailDrawer } = usePayoutDetailDrawer();

  if (!payouts?.length) return null;

  return (
    <Card
      p={rem(16)}
      radius="md"
      withBorder
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(8),
        backgroundColor: theme.colors.blue[0],
        borderColor: theme.colors.blue[1],
      })}
    >
      <Text
        component="p"
        m={0}
        c="neutral.5"
        fz={rem(12)}
        lh={1.4}
        style={{
          letterSpacing: '0.1px',
        }}
      >
        Current statement payments
      </Text>

      <Stack gap={rem(8)}>
        {payouts.map((payout, index) => (
          <StatementPaymentSummaryItem
            key={payout.id || `${payout.date}-${payout.total}-${index}`}
            currency={currency}
            onOpen={() => openPayoutDetailDrawer(payout.id)}
            payout={payout}
          />
        ))}
      </Stack>
    </Card>
  );
};

const StatementPaymentSummaryItem = ({
  payout,
  currency,
  onOpen,
}: {
  payout: NonNullable<OwnerStatement['payouts']>[number];
  currency?: OwnerStatement['currency'];
  onOpen: () => void;
}) => {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Anchor
        component="button"
        m={0}
        c="neutral.8"
        fz={rem(14)}
        lh={1.4}
        style={{
          letterSpacing: '-0.09px',
        }}
        onClick={onOpen}
      >
        {`Payment on ${utc(payout.date).format('ll')}`}
      </Anchor>
      <Text
        component="p"
        m={0}
        c="neutral.8"
        fz={rem(14)}
        lh={1.4}
        ta="right"
        style={{
          letterSpacing: '-0.09px',
        }}
      >
        {formatCurrency(payout.total / 100, currency)}
      </Text>
    </Group>
  );
};
